import React, { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { Badge } from '@/components/ui/badge';
import { DialogFooter } from '@/components/ui/dialog';
import { CsvRecordTable } from './CsvRecordTable';
import { CsvRecord } from './types';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';

interface CsvReviewStepProps {
  records: CsvRecord[];
  onImportComplete: () => void;
  onBack: () => void;
}

export const CsvReviewStep = ({
  records,
  onImportComplete,
  onBack
}: CsvReviewStepProps) => {
  const queryClient = useQueryClient();
  const [localRecords, setLocalRecords] = useState<CsvRecord[]>(records);
  
  const validRecords = localRecords.filter(r => r.isValid && !r.isDuplicate);
  const duplicateRecords = localRecords.filter(r => r.isDuplicate);
  const selectedRecords = localRecords.filter(r => r.selected && r.isValid && !r.isDuplicate);
  const selectAll = validRecords.length > 0 && selectedRecords.length === validRecords.length;

  const importMutation = useMutation({
    mutationFn: async (recordsToImport: CsvRecord[]) => {
      const importPromises = recordsToImport.map(async (record) => {
        const { data, error } = await supabase
          .from('user_imports')
          .insert({
            email: record.email,
            first_name: record.first_name,
            last_name: record.last_name,
            role: record.role,
            status: 'imported',
            validation_errors: record.validationErrors.length > 0 ? record.validationErrors : null,
            original_row_data: {
              email: record.email,
              first_name: record.first_name,
              last_name: record.last_name,
              role: record.role
            }
          })
          .select()
          .single();

        if (error) {
          console.error('Import error for', record.email, ':', error);
          throw error;
        }

        return data;
      });

      const results = await Promise.all(importPromises);
      return results;
    },
    onSuccess: () => {
      toast.success(`Successfully imported ${selectedRecords.length} records`);
      queryClient.invalidateQueries({ queryKey: ['user-imports'] });
      onImportComplete();
    },
    onError: (error) => {
      console.error('Import failed:', error);
      toast.error('Failed to import records: ' + error.message);
    }
  });

  const handleToggleSelectAll = () => {
    setLocalRecords(prev => prev.map(record => ({
      ...record,
      selected: (record.isValid && !record.isDuplicate) ? !selectAll : record.selected
    })));
  };

  const handleToggleRecordSelection = (recordId: string) => {
    setLocalRecords(prev => prev.map(record => 
      record.id === recordId ? { ...record, selected: !record.selected } : record
    ));
  };

  const handleImport = () => {
    if (selectedRecords.length === 0) return;
    importMutation.mutate(selectedRecords);
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <div className="flex items-center space-x-2">
            <Checkbox
              id="selectAll"
              checked={selectAll}
              onCheckedChange={handleToggleSelectAll}
            />
            <Label htmlFor="selectAll">Select All Valid Records</Label>
          </div>
          <Badge variant="secondary">
            {selectedRecords.length} of {validRecords.length} selected
          </Badge>
          {duplicateRecords.length > 0 && (
            <Badge variant="destructive">
              {duplicateRecords.length} duplicates
            </Badge>
          )}
        </div>
        <Badge variant="outline">
          Total: {localRecords.length} records
        </Badge>
      </div>

      <CsvRecordTable 
        records={localRecords} 
        onToggleSelection={handleToggleRecordSelection} 
      />

      <DialogFooter>
        <Button variant="outline" onClick={onBack}>
          Back
        </Button>
        <Button 
          onClick={handleImport}
          disabled={selectedRecords.length === 0 || importMutation.isPending}
        >
          {importMutation.isPending ? 'Importing...' : `Import ${selectedRecords.length} Record${selectedRecords.length !== 1 ? 's' : ''}`}
        </Button>
      </DialogFooter>
    </div>
  );
};
